<template>
  <div :class="[getClassedBasedOnDirection('arrow-button'), ...buttonOptionalClasses]">
    <button
      :aria-label="buttonLabel"
      class="owc-subnavigation__arrow-button wb-round-button wb-round-button--medium"
    >
      <wb-icon
        :class="['wb-round-button__icon', ...iconOptionalClasses]"
        :name="iconName"
        aria-hidden="true"
      ></wb-icon>
    </button>
    <div
      v-if="hasBackground"
      :class="[
        'owc-subnavigation__background',
        getClassedBasedOnDirection('background'),
        ...backgroundOptionalClasses,
      ]"
    />
  </div>
</template>

<script lang="ts">
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';

export default ComponentInitializeDispatcher.extend({
  name: 'owc-subnavigation-arrow',
  props: {
    hasBackground: {
      required: false,
      type: Boolean,
      default: true,
    },
    buttonLabel: {
      required: false,
      type: String,
    },
    buttonOptionalClasses: {
      required: false,
      type: Array,
      default: function() {
        return [];
      },
    },
    iconName: {
      required: true,
      type: String,
    },
    iconOptionalClasses: {
      required: false,
      type: Array,
      default: function() {
        return [];
      },
    },
    backgroundOptionalClasses: {
      required: false,
      type: Array,
      default: function() {
        return [];
      },
    },
    direction: {
      required: false,
      type: String,
    },
  },
  data() {
    return {
      prefixClass: 'owc-subnavigation',
    };
  },
  computed: {
    shouldShowBackground: function(): boolean {
      return this.direction !== '';
    },
  },
  methods: {
    getClassedBasedOnDirection: function(classToAppend: string): string {
      const resultClass = `${this.prefixClass}__${classToAppend}`;
      return this.direction ? `${resultClass}-${this.direction}` : resultClass;
    },
  },
});
</script>

<style lang="scss">
@import '@owc-styles/utils/z-index-list';
@import '@owc-styles/utils/rtl-utils';

.owc-subnavigation {
  /* Both these variables are directly attached to JS
 * If you change these, don't forget to change them on the ./Subnavigation.config
 * **/
  $SUBNAVIGATION_ITEM_GAP: 16;
  $SUBNAVIGATION_ARROW_BACKGROUND_WIDTH: 90;

  /*
 * Calculate gradient step in which the background should be completly
 * white, since the gradient cannot be uniform (related to width) othewrwise
 * text is visible behind the arrow button
 *
 * So instead of 100% width we calculate the percentage needed to create a
 * complete gradient from Left: 0 until the end of the button and not the
 * button container:
 *
 *  subNavigationItem |      [ > ]      |        //Where [>] is the arrow button
 *                     ---------|--------
 *                     0       100%
 *                      gradient   white
 */
  $wbMediumButtonSize: 48;
  $gradientPercentage: math.percentage(
    math.div($wbMediumButtonSize - $SUBNAVIGATION-ITEM-GAP, $SUBNAVIGATION-ARROW-BACKGROUND-WIDTH)
  );

  &__arrow-button {
    position: relative;

    // Needs to be 1 step higher than the arrow backgrounds
    z-index: 2;

    display: inline-flex;

    flex-shrink: 0;

    max-height: inherit;

    &-prev {
      position: absolute;
      top: 0;
      left: 0;

      display: flex;
      align-items: center;

      height: 100%;
      max-height: inherit;
    }
    &-next {
      position: absolute;
      top: 0;
      right: 0;

      display: flex;
      align-items: center;

      height: 100%;
      max-height: inherit;
    }
    &:active {
      color: var(--wb-grey-85);
    }
  }
  &__background {
    position: absolute;
    top: 0;
    z-index: $z-index-default;

    display: block;

    width: $SUBNAVIGATION_ARROW_BACKGROUND_WIDTH + 0px;
    height: 40px;

    content: '';

    &-prev {
      left: 0;

      max-height: inherit;

      background: linear-gradient(to left, rgba($wb-white, 0) 0%, var(--wb-white) $gradientPercentage);
    }
    &-next {
      right: 0;

      max-height: inherit;

      background: linear-gradient(to right, rgba($wb-white, 0) 0%, var(--wb-white) $gradientPercentage);
    }
  }
  & .wb-round-button {
    border: none;

    &__icon {
      /*
    * Ensure that the icon shows on top of the backgorund gradient on desktop
    */
      z-index: $z-index-default;

      max-height: inherit;

      color: var(--wb-grey-20);
    }
  }
}

@include rtl-rule('.owc-subnavigation', false) {
  &__sliding-cta-buttons {
    -webkit-transform: translateX(-50%);
    transform: translateX(-50%);
  }
}
</style>
